<!-- Diagnosis_History.vue — graph-sourced -->
<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <!-- Header -->
    <div class="px-4 py-2.5 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <h3 class="text-base font-semibold text-gray-900">Diagnosis History</h3>
        <button
          @click="$emit('refresh')"
          :disabled="loading"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FontAwesomeIcon
            :icon="['fas', 'sync']"
            :class="{ 'animate-spin': loading }"
            class="text-sm"
          />
        </button>
      </div>
    </div>

    <!-- AI Summary Banner -->
    <div v-if="summary" class="px-4 py-3 bg-purple-50 border-b border-purple-100">
      <div class="flex items-start gap-2">
        <FontAwesomeIcon :icon="['fas', 'wand-magic-sparkles']" class="text-[10px] text-purple-500 mt-1 flex-shrink-0" />
        <p class="text-xs text-purple-800 leading-relaxed">{{ summary }}</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <!-- Empty -->
    <div v-else-if="enrichedConditions.length === 0" class="text-center py-8 px-4">
      <FontAwesomeIcon :icon="['fas', 'file-medical']" class="text-2xl text-gray-300 mb-2" />
      <p class="text-sm text-gray-500">No diagnosis history found</p>
    </div>

    <div v-else class="p-4">
      <!-- Search -->
      <div v-if="enrichedConditions.length > 5" class="mb-3">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search diagnoses..."
          class="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
        />
      </div>

      <!-- Stats -->
      <div class="grid grid-cols-3 gap-2 mb-3">
        <div class="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
          <div class="text-xl font-semibold text-gray-900">{{ enrichedConditions.length }}</div>
          <div class="text-xs text-gray-600 mt-0.5">Total</div>
        </div>
        <div class="bg-blue-50 rounded-lg p-2.5 border border-blue-200">
          <div class="text-xl font-semibold text-blue-900">{{ hccCount }}</div>
          <div class="text-xs text-blue-700 mt-0.5">HCC Codes</div>
        </div>
        <div class="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
          <div class="text-xl font-semibold text-gray-900">{{ uniqueProviders }}</div>
          <div class="text-xs text-gray-600 mt-0.5">Providers</div>
        </div>
      </div>

      <!-- Condition Cards -->
      <div class="space-y-2">
        <div
          v-for="(dx, index) in filteredConditions"
          :key="`${dx.code}-${index}`"
          class="border border-gray-200 rounded hover:shadow-sm transition-shadow"
        >
          <div
            @click="toggleExpanded(dx.code)"
            class="px-2.5 py-1.5 cursor-pointer hover:bg-gray-50"
          >
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2 min-w-0 flex-1">
                <div
                  :class="[
                    'w-5 h-5 rounded flex items-center justify-center text-xs font-medium shrink-0',
                    dx.hcc_code ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                  ]"
                >
                  {{ dx.hcc_code || 'DX' }}
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <span class="font-medium text-gray-900 text-xs">{{ dx.code }}</span>
                    <span v-if="dx.hcc_code" class="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                      HCC {{ dx.hcc_code }}
                    </span>
                  </div>
                  <div class="text-xs text-gray-600 mt-0.5">
                    {{ dx.description || 'No description available' }}
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <span class="text-[10px] text-gray-400">{{ dx.claim_count }} claim{{ dx.claim_count !== 1 ? 's' : '' }}</span>
                <FontAwesomeIcon
                  :icon="['fas', expanded[dx.code] ? 'chevron-up' : 'chevron-down']"
                  class="text-gray-400 text-xs"
                />
              </div>
            </div>

            <!-- Expanded: claim dates for this condition -->
            <div v-if="expanded[dx.code]" class="mt-1.5 pt-1.5 border-t border-gray-100">
              <div class="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div>
                  <span class="text-gray-500">Last Billed:</span>
                  <span class="font-medium text-gray-900 ml-1">{{ formatDate(dx.last_date) }}</span>
                </div>
                <div>
                  <span class="text-gray-500">First Seen:</span>
                  <span class="font-medium text-gray-900 ml-1">{{ formatDate(dx.first_date) }}</span>
                </div>
                <div v-if="dx.providers.length">
                  <span class="text-gray-500">Providers:</span>
                  <span class="font-medium text-gray-900 ml-1 sensitive">{{ dx.providers.join(', ') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'Diagnosis_History',
  components: { FontAwesomeIcon },
  props: {
    conditions: { type: Array, default: () => [] },
    claims: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    summary: { type: String, default: '' }
  },
  emits: ['refresh'],
  data() {
    return {
      searchQuery: '',
      expanded: {},
    };
  },
  computed: {
    enrichedConditions() {
      // Build a map of condition code → { dates, providers, claim_count }
      const map = {};
      for (const c of this.claims) {
        for (const cond of (c.conditions || [])) {
          const code = cond.code;
          if (!code) continue;
          if (!map[code]) {
            map[code] = {
              code,
              description: cond.description || '',
              dates: [],
              providers: new Set(),
              hcc_code: null,
            };
          }
          if (c.CLM_FROM_DT) map[code].dates.push(c.CLM_FROM_DT);
          for (const p of (c.practitioners || [])) {
            if (p.name) map[code].providers.add(p.name);
          }
        }
      }
      // Also include conditions from the all_conditions prop (deduplicated)
      for (const cond of this.conditions) {
        if (cond.code && !map[cond.code]) {
          map[cond.code] = {
            code: cond.code,
            description: cond.description || '',
            dates: [],
            providers: new Set(),
            hcc_code: null,
          };
        }
      }
      return Object.values(map).map(c => ({
        code: c.code,
        description: c.description,
        hcc_code: c.hcc_code,
        claim_count: c.dates.length,
        first_date: c.dates.sort()[0] || '',
        last_date: c.dates.sort().pop() || '',
        providers: [...c.providers].slice(0, 3),
      })).sort((a, b) => (b.last_date || '').localeCompare(a.last_date || ''));
    },
    hccCount() {
      return this.enrichedConditions.filter(c => c.hcc_code).length;
    },
    uniqueProviders() {
      const all = new Set();
      for (const c of this.enrichedConditions) {
        for (const p of c.providers) all.add(p);
      }
      return all.size;
    },
    filteredConditions() {
      if (!this.searchQuery) return this.enrichedConditions;
      const q = this.searchQuery.toLowerCase();
      return this.enrichedConditions.filter(dx =>
        dx.code?.toLowerCase().includes(q) ||
        dx.description?.toLowerCase().includes(q)
      );
    }
  },
  methods: {
    toggleExpanded(code) {
      this.expanded[code] = !this.expanded[code];
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    }
  }
};
</script>
